import { DEFAULT_MODEL, type AgentConfig, type AgentMode, type AgentPreset, type ChatModel, type SavedInstruction, type SavedPrompt, type ToolsConfig } from "@acme/util";
import { atom } from "jotai";
import { atomWithStorage } from "jotai/utils";

// =============================================================================
// POPUP MANAGEMENT
// =============================================================================

export type ModelPopupType =
    | 'prompt'
    | 'instruction'
    | 'attach-file'
    | 'tools-config'
    | 'agent-config'
    | '';

export const modelPopupAtom = atom<{ open: boolean, type: ModelPopupType }>({ open: false, type: '' })
export const popupSettingsInstructionTriggerAtom = atom<{ type?: 'note' | 'chat' | null, instruction?: string }>({})

// =============================================================================
// SAVED ITEMS
// =============================================================================

export const savedInstructionsAtom = atomWithStorage<SavedInstruction[]>("savedInstructions", [])
export const savedPromptsAtom = atomWithStorage<SavedPrompt[]>("savedPrompts", [])

export const newInstructionPopupAtom = atom(false)
export const newInstructionAtom = atom<{ name: string, defaultModel: ChatModel, description: string, instructions: string, isSync?: boolean, isUpdateId?: string, isUpdateSync?: boolean }>({
    name: "", defaultModel: DEFAULT_MODEL, description: "", instructions: "", isSync: false, isUpdateId: "", isUpdateSync: false
})
export const newPromptAtom = atom<{ name: string, prompt: string, isSync?: boolean, isUpdateId?: string, isUpdateSync?: boolean }>({
    name: "", prompt: ""
})

// =============================================================================
// TOOLS & AGENT CONFIGURATION
// =============================================================================

/** Default tools enabled by default */
export const DEFAULT_ENABLED_TOOLS = ['calculator', 'dateTime', 'webScraper', 'apiCall'];

/** Default agent configuration */
export const DEFAULT_AGENT_CONFIG: AgentConfig = {
    preset: 'assistant',
    maxSteps: 10,
    temperature: 0.7,
};

/** Default tools configuration */
export const DEFAULT_TOOLS_CONFIG: ToolsConfig = {
    enabledTools: DEFAULT_ENABLED_TOOLS,
    mcpServers: [],
};

/** Global tools enabled state (persisted) */
export const globalToolsConfigAtom = atomWithStorage<ToolsConfig>('globalToolsConfig', DEFAULT_TOOLS_CONFIG);

/** Global agent config (persisted) */
export const globalAgentConfigAtom = atomWithStorage<AgentConfig>('globalAgentConfig', DEFAULT_AGENT_CONFIG);

/** Current agent mode for active session (persisted) */
export const activeAgentModeAtom = atomWithStorage<AgentMode>('activeAgentMode', 'chat');

/** Whether tools are currently active in compose (persisted) */
export const toolsActiveAtom = atomWithStorage<boolean>('toolsActiveState', false);

/** MCP server configurations (persisted) */
export interface MCPServerUIConfig {
    id: string;
    name: string;
    url: string;
    type: 'http' | 'sse';
    enabled: boolean;
    description?: string;
    /**
     * Extra request headers, e.g. `{ Authorization: 'Bearer …' }`. Without
     * these, any MCP server behind auth rejects the connection — which was
     * every server that isn't fully public.
     */
    headers?: Record<string, string>;
}

export const mcpServersConfigAtom = atomWithStorage<MCPServerUIConfig[]>('mcpServersConfig', []);

/** Agent presets with metadata for UI */
export const AGENT_PRESETS: Record<AgentPreset, { name: string; icon: string; description: string }> = {
    assistant: {
        name: 'Assistant',
        icon: '💬',
        description: 'General purpose helpful assistant',
    },
    researcher: {
        name: 'Researcher',
        icon: '🔬',
        description: 'Research with web search & URL fetching',
    },
    coder: {
        name: 'Coder',
        icon: '💻',
        description: 'Programming and code assistance',
    },
    analyst: {
        name: 'Analyst',
        icon: '📊',
        description: 'Data analysis and calculations',
    },
    writer: {
        name: 'Writer',
        icon: '✍️',
        description: 'Creative writing and content',
    },
    custom: {
        name: 'Custom',
        icon: '⚙️',
        description: 'Define your own agent behavior',
    },
};


